import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/Layout";
import {
  censorService,
  Post,
  PostsResponse,
  AdminStats,
} from "../../services/censorService";
import { apiClient } from "../../services/apiClient";
import { showToast } from "../../utils/toastUtils";
import { formatDateTime } from "../../utils/dateFormatter";

import {
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  Search,
  Eye,
  Loader2,
  FileText,
  Tag,
  Calendar,
  Link,
  View,
  X,
  AlertTriangle,
  Shield,
  AlertCircle,
  Check,
  X as XIcon,
} from "lucide-react";
import "./../../styles/member/cencor.css";
import emptyDataImg from "../../assets/icons/empty-data.svg";

// Removed unused formatNow function

const Cencor: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    deleted: 0,
  });
  const [tab, setTab] = useState<"pending" | "approved" | "rejected">(
    "pending"
  );
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [moderationResult, setModerationResult] = useState<any>(null);
  const [checkingModeration, setCheckingModeration] = useState(false);
  const [postsModerationResults, setPostsModerationResults] = useState<{
    [key: number]: any;
  }>({});
  const [checkingAllModeration, setCheckingAllModeration] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertPostId, setRevertPostId] = useState<number | null>(null);
  const [revertPostTitle, setRevertPostTitle] = useState<string>("");
  const [revertAction, setRevertAction] = useState<"approve" | "reject" | null>(
    null
  );
  const [revertReason, setRevertReason] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingPost, setIsUpdatingPost] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkApprove, setShowBulkApprove] = useState(false);
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  // Lọc bài viết dựa trên danh mục và tìm kiếm
  const filtered = posts.filter((post) => {
    const matchesCategory =
      !selectedCategory || post.category?.name === selectedCategory;
    const matchesSearch =
      !searchQuery ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.author?.fullName
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      post.author?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.metaDescription?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  // Tính toán trạng thái chọn tất cả
  const allSelected =
    filtered.length > 0 && selectedIds.length === filtered.length;
  const someSelected =
    selectedIds.length > 0 && selectedIds.length < filtered.length;

  // Tải dữ liệu từ API
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, postsData] = await Promise.all([
        censorService.getAdminStats(),
        getPostsByTab(tab, page),
      ]);
      setStats(statsData);
      setPosts(postsData.posts);
      setAllPosts(postsData.posts);
      setTotalPages(postsData.totalPages);
    } catch (err: any) {
      setError(err.response?.data?.message || "Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, [tab, page]);

  const getPostsByTab = async (
    currentTab: string,
    currentPage: number = 1
  ): Promise<PostsResponse> => {
    switch (currentTab) {
      case "pending":
        return await censorService.getPendingPosts(currentPage, 10);
      case "approved":
        return await censorService.getApprovedPosts(currentPage, 10);
      case "rejected":
        return await censorService.getRejectedPosts(currentPage, 10);
      default:
        return await censorService.getPendingPosts(currentPage, 10);
    }
  };

  const checkAllPostsModeration = useCallback(async () => {
    const currentPosts = filtered.length > 0 ? filtered : posts;
    if (currentPosts.length === 0) return;

    setCheckingAllModeration(true);
    const results: { [key: number]: any } = {};

    try {
      // Kiểm tra từng bài viết
      for (const post of currentPosts) {
        try {
          const response = await apiClient.post("/posts/moderate", {
            title: post.title || "",
            content: post.content || "",
            imageUrls: post.featuredImage ? [post.featuredImage] : [],
          });

          if (response.ok) {
            const result = await response.json();
            results[post.id] = result;
          }
        } catch (error) {
          // Bỏ qua lỗi cho bài viết này
        }
      }

      setPostsModerationResults(results);
    } catch (error) {
      // Không hiển thị lỗi
    } finally {
      setCheckingAllModeration(false);
    }
  }, [filtered, posts]);

  // Tải dữ liệu khi component mount và khi tab thay đổi
  useEffect(() => {
    loadData();
  }, [tab, page, loadData]);

  // Tự động kiểm tra moderation khi posts thay đổi (chỉ khi load data mới)
  useEffect(() => {
    if (posts.length > 0 && !loading && !isUpdatingPost) {
      checkAllPostsModeration();
    }
  }, [posts.length, loading, isUpdatingPost, checkAllPostsModeration]);

  // Reset trang và bộ lọc khi tab thay đổi
  useEffect(() => {
    setPage(1);
    setSelectedCategory("");
    setSearchQuery("");
    setSelectedIds([]);
  }, [tab]);

  // Xử lý lọc danh mục
  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    setPage(1);
  };

  // Xử lý tìm kiếm
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  // Xử lý chọn tất cả
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((post) => post.id));
    }
  };

  // Xử lý chọn một bài viết
  const toggleSelect = (postId: number) => {
    setSelectedIds((prev) =>
      prev.includes(postId)
        ? prev.filter((id) => id !== postId)
        : [...prev, postId]
    );
  };

  // Lấy danh mục duy nhất từ bài viết
  const getUniqueCategories = () => {
    const categories = allPosts
      .map((post) => post.category?.name)
      .filter((name): name is string => !!name)
      .filter((name, index, arr) => arr.indexOf(name) === index);
    return categories;
  };

  // Mở chi tiết bài viết
  const openDetail = async (post: Post) => {
    setSelectedPost(post);
    setShowDetail(true);
    setShowRejectForm(false);
    setRejectReason("");
    setModerationResult(null);

    // Kiểm tra nội dung nhạy cảm
    await checkModeration(post);
  };

  const checkModeration = async (post: Post) => {
    if (!post) return;

    setCheckingModeration(true);
    try {
      const response = await apiClient.post("/posts/moderate", {
        title: post.title || "",
        content: post.content || "",
        imageUrls: post.featuredImage ? [post.featuredImage] : [],
      });

      if (response.ok) {
        const result = await response.json();
        setModerationResult(result);
      }
    } catch (error) {
      // Không hiển thị lỗi nếu không kiểm tra được
    } finally {
      setCheckingModeration(false);
    }
  };

  // Mở từ chối bài viết
  const openReject = (post: Post) => {
    setSelectedPost(post);
    setShowRejectForm(true);
    setShowDetail(false);
    setRejectReason("");
  };

  // Mở modal xác nhận từ chối bài viết đã duyệt
  const handleRejectApproved = (postId: number, postTitle: string) => {
    setRevertPostId(postId);
    setRevertPostTitle(postTitle);
    setRevertAction("reject");
    setRevertReason("");
    setShowRevertModal(true);
  };

  // Mở modal xác nhận duyệt bài viết bị từ chối
  const handleApproveRejected = (postId: number, postTitle: string) => {
    setRevertPostId(postId);
    setRevertPostTitle(postTitle);
    setRevertAction("approve");
    setRevertReason("");
    setShowRevertModal(true);
  };

  // Xác nhận thay đổi trạng thái
  const confirmRevertAction = async () => {
    if (!revertPostId || !revertAction) return;

    // Kiểm tra lý do
    if (!revertReason.trim()) {
      showToast.error("Vui lòng nhập lý do trước khi xác nhận!");
      return;
    }

    try {
      setLoading(true);
      setIsUpdatingPost(true);

      if (revertAction === "approve") {
        // Duyệt bài viết bị từ chối
        await censorService.approvePost(revertPostId, {
          status: "approved",
          rejectionReason: revertReason.trim(),
        });

        // Chỉ cập nhật bài viết cụ thể - loại bỏ khỏi danh sách hiện tại
        setPosts((prevPosts) =>
          prevPosts.filter((post) => post.id !== revertPostId)
        );

        // Cập nhật stats
        setStats((prevStats) => ({
          ...prevStats,
          rejected: Math.max(0, prevStats.rejected - 1),
          approved: prevStats.approved + 1,
        }));

        showToast.success("Bài viết đã được duyệt thành công!");
      } else if (revertAction === "reject") {
        // Từ chối bài viết đã duyệt
        await censorService.approvePost(revertPostId, {
          status: "rejected",
          rejectionReason: revertReason.trim(),
        });

        // Chỉ cập nhật bài viết cụ thể - loại bỏ khỏi danh sách hiện tại
        setPosts((prevPosts) =>
          prevPosts.filter((post) => post.id !== revertPostId)
        );

        // Cập nhật stats
        setStats((prevStats) => ({
          ...prevStats,
          approved: Math.max(0, prevStats.approved - 1),
          rejected: prevStats.rejected + 1,
        }));

        showToast.success("Bài viết đã bị từ chối!");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Có lỗi xảy ra khi thay đổi trạng thái bài viết"
      );
    } finally {
      setLoading(false);
      setIsUpdatingPost(false);
      setShowRevertModal(false);
      setRevertPostId(null);
      setRevertPostTitle("");
      setRevertAction(null);
      setRevertReason("");
    }
  };

  // Xử lý duyệt bài viết
  const handleApprove = async (postId: number) => {
    try {
      setLoading(true);
      setIsUpdatingPost(true);
      await censorService.approvePost(postId, { status: "approved" });

      // Loại bỏ bài viết khỏi danh sách hiện tại (vì nó đã chuyển sang tab khác)
      setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId));

      // Cập nhật stats
      setStats((prevStats) => ({
        ...prevStats,
        pending: Math.max(0, prevStats.pending - 1),
        approved: prevStats.approved + 1,
      }));

      setShowRejectForm(false);
      setShowDetail(false);
      showToast.success("Bài viết đã được duyệt thành công!");
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Có lỗi xảy ra khi duyệt bài viết"
      );
    } finally {
      setLoading(false);
      setIsUpdatingPost(false);
    }
  };

  // Xử lý xác nhận từ chối bài viết
  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      showToast.error("Vui lòng nhập lý do từ chối");
      return;
    }
    if (!selectedPost) return;

    try {
      setLoading(true);
      setIsUpdatingPost(true);
      await censorService.approvePost(selectedPost.id, {
        status: "rejected",
        rejectionReason: rejectReason,
      });

      // Loại bỏ bài viết khỏi danh sách hiện tại (vì nó đã chuyển sang tab khác)
      setPosts((prevPosts) =>
        prevPosts.filter((post) => post.id !== selectedPost.id)
      );

      // Cập nhật stats
      setStats((prevStats) => ({
        ...prevStats,
        pending: Math.max(0, prevStats.pending - 1),
        rejected: prevStats.rejected + 1,
      }));

      setShowRejectForm(false);
      setShowDetail(false);
      setRejectReason("");
      showToast.success("Bài viết đã bị từ chối!");
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Có lỗi xảy ra khi từ chối bài viết"
      );
    } finally {
      setLoading(false);
      setIsUpdatingPost(false);
    }
  };

  const categoryOptions = [
    { value: "", label: "Tất cả danh mục" },
    ...getUniqueCategories().map((cat) => ({ value: cat, label: cat })),
  ];

  // Xử lý duyệt hàng loạt
  const handleBulkApprove = () => {
    if (selectedIds.length === 0) return;
    setShowBulkApprove(true);
  };

  const onConfirmBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await censorService.bulkApprovePosts(selectedIds);
      // Cập nhật danh sách bài viết sau khi duyệt
      setPosts((prev) => prev.filter((post) => !selectedIds.includes(post.id)));
      setAllPosts((prev) =>
        prev.filter((post) => !selectedIds.includes(post.id))
      );
      setSelectedIds([]);
      setShowBulkApprove(false);
      // Cập nhật stats
      setStats((prevStats) => ({
        ...prevStats,
        pending: Math.max(0, prevStats.pending - selectedIds.length),
        approved: prevStats.approved + selectedIds.length,
      }));
      // Hiển thị thông báo thành công
      showToast.success(result.message);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Có lỗi xảy ra khi duyệt bài viết";
      setError(errorMessage);
      // Nếu có một số bài viết đã được duyệt thành công, cập nhật lại danh sách
      if (errorMessage.includes("Duyệt thành công")) {
        await loadData();
      }
    } finally {
      setLoading(false);
    }
  };

  // Xử lý từ chối hàng loạt
  const handleBulkReject = () => {
    if (selectedIds.length === 0) return;
    setShowBulkReject(true);
    setBulkRejectReason("");
  };

  const onConfirmBulkReject = async () => {
    if (selectedIds.length === 0 || !bulkRejectReason.trim()) {
      showToast.error("Vui lòng nhập lý do từ chối");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await censorService.bulkRejectPosts(
        selectedIds,
        bulkRejectReason
      );
      // Cập nhật danh sách bài viết sau khi từ chối
      setPosts((prev) => prev.filter((post) => !selectedIds.includes(post.id)));
      setAllPosts((prev) =>
        prev.filter((post) => !selectedIds.includes(post.id))
      );
      setSelectedIds([]);
      setShowBulkReject(false);
      setBulkRejectReason("");
      // Cập nhật stats
      setStats((prevStats) => ({
        ...prevStats,
        pending: Math.max(0, prevStats.pending - selectedIds.length),
        rejected: prevStats.rejected + selectedIds.length,
      }));
      // Hiển thị thông báo thành công
      showToast.success(result.message);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Có lỗi xảy ra khi từ chối bài viết";
      setError(errorMessage);
      // Nếu có một số bài viết đã được từ chối thành công, cập nhật lại danh sách
      if (errorMessage.includes("Từ chối thành công")) {
        await loadData();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="censor-container">
        <div className="censor-header">
          <h2 className="censor-title">Kiểm duyệt bài viết</h2>
          <p className="censor-subtitle">
            Xem xét và phê duyệt các bài viết được gửi lên
          </p>
        </div>

        {/* Thống kê */}
        <div className="censor-stats">
          <div className="censor-card">
            <Clock className="censor-icon text-pending" />
            <div>
              <h3>Chờ duyệt</h3>
              <p>
                {loading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  stats.pending
                )}
              </p>
            </div>
          </div>

          <div className="censor-card">
            <CheckCircle className="censor-icon text-approved" />
            <div>
              <h3>Đã duyệt</h3>
              <p>
                {loading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  stats.approved
                )}
              </p>
            </div>
          </div>

          <div className="censor-card">
            <XCircle className="censor-icon text-rejected" />
            <div>
              <h3>Từ chối</h3>
              <p>
                {loading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  stats.rejected
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Bộ lọc */}
        <div className="censor-filter">
          <span className="filter-title">
            <Filter size={18} /> Bộ lọc
          </span>
          <div className="censor-filter-action">
            <div className="filter-dropdown">
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setCategoryOpen((v) => !v)}
              >
                {categoryOptions.find((o) => o.value === selectedCategory)
                  ?.label || "Tất cả danh mục"}
                <span className="dropdown-arrow">▼</span>
              </button>
              {categoryOpen && (
                <div className="dropdown-menu">
                  {categoryOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={`dropdown-item${
                        selectedCategory === opt.value ? " active" : ""
                      }`}
                      onClick={() => {
                        handleCategoryFilter(opt.value);
                        setCategoryOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="search-box">
              <Search className="filter-icon" />
              <input
                type="text"
                placeholder="Tìm kiếm theo tiêu đề, tác giả..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleSearch(searchQuery)
                }
              />
              {(selectedCategory || searchQuery) && (
                <button
                  onClick={() => {
                    setSelectedCategory("");
                    setSearchQuery("");
                  }}
                  className="delete-search"
                >
                  Xóa bộ lọc
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab */}
        <div className="censor-tabs">
          <button
            className={tab === "pending" ? "active" : ""}
            onClick={() => setTab("pending")}
          >
            Chờ duyệt
          </button>
          <button
            className={tab === "approved" ? "active" : ""}
            onClick={() => setTab("approved")}
          >
            Đã duyệt
          </button>
          <button
            className={tab === "rejected" ? "active" : ""}
            onClick={() => setTab("rejected")}
          >
            Từ chối
          </button>
        </div>

        {/* Thông báo lỗi */}
        {error && <div className="error-message">{error}</div>}

        {/* Danh sách */}
        <div className="censor-list">
          {loading ? (
            <div className="loading-state">
              <Loader2 className="animate-spin" size={32} />
              <p>Đang tải dữ liệu...</p>
            </div>
          ) : filtered.length > 0 ? (
            <>
              <h3>Danh sách bài viết ({filtered.length})</h3>
              <p>
                Tất cả bài viết trong mục{" "}
                <b>
                  {tab === "pending"
                    ? "Chờ duyệt"
                    : tab === "approved"
                    ? "Đã duyệt"
                    : tab === "rejected"
                    ? "Từ chối"
                    : tab}
                </b>
                {selectedCategory && (
                  <span>
                    • Danh mục: <b>{selectedCategory}</b>
                  </span>
                )}
                {searchQuery && (
                  <span>
                    • Tìm kiếm: <b>"{searchQuery}"</b>
                  </span>
                )}
              </p>

              {/* Bulk actions */}
              {selectedIds.length > 0 && (
                <div className="cat-bulk-bar">
                  <span>Đã chọn {selectedIds.length}</span>
                  <div className="card-actions">
                    {tab === "pending" && (
                      <>
                        <button
                          type="button"
                          className="status-approve"
                          disabled={loading}
                          onClick={handleBulkApprove}
                        >
                          <CheckCircle size={16} />
                          Duyệt tất cả
                        </button>
                        <button
                          type="button"
                          className="status-reject"
                          disabled={loading}
                          onClick={handleBulkReject}
                        >
                          <XCircle size={16} />
                          Từ chối tất cả
                        </button>
                      </>
                    )}
                    {tab === "approved" && (
                      <>
                        <button
                          type="button"
                          className="status-reject"
                          disabled={loading}
                          onClick={handleBulkReject}
                        >
                          <XCircle size={16} />
                          Từ chối tất cả
                        </button>
                      </>
                    )}
                    {tab === "rejected" && (
                      <>
                        <button
                          type="button"
                          className="status-approve"
                          disabled={loading}
                          onClick={handleBulkApprove}
                        >
                          <CheckCircle size={16} />
                          Duyệt tất cả
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="custom-btn"
                      onClick={() => setSelectedIds([])}
                    >
                      Bỏ chọn
                    </button>
                  </div>
                </div>
              )}

              <table>
                <thead>
                  <tr>
                    <th className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = someSelected;
                        }}
                        onChange={toggleSelectAll}
                        aria-label="Chọn tất cả"
                      />
                    </th>
                    <th>ID</th>
                    <th>Bài viết</th>
                    <th>Tác giả</th>
                    <th>Danh mục</th>
                    <th>Ngày gửi</th>
                    <th>Trạng thái</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((post) => (
                    <tr key={post.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(post.id)}
                          onChange={() => toggleSelect(post.id)}
                          aria-label={`Chọn bài viết ${post.title}`}
                        />
                      </td>
                      <td>PM{post.id.toString().padStart(3, "0")}</td>
                      <td>
                        <div className="article-info">
                          <img
                            src={
                              post.featuredImage ||
                              "/images/default-article.jpg"
                            }
                            alt={post.title}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "/images/default-article.jpg";
                            }}
                          />
                          <div>
                            <div className="title-block">
                              {post.title}
                              {postsModerationResults[post.id] &&
                                !postsModerationResults[post.id].isSafe && (
                                  <span
                                    className="moderation-indicator"
                                    title={
                                      postsModerationResults[post.id]
                                        .severity === "block"
                                        ? "Nội dung bị cấm"
                                        : "Nội dung nhạy cảm"
                                    }
                                  >
                                    {postsModerationResults[post.id]
                                      .severity === "block" ? (
                                      <AlertCircle
                                        size={16}
                                        className="moderation-icon-block"
                                      />
                                    ) : (
                                      <AlertTriangle
                                        size={16}
                                        className="moderation-icon-warn"
                                      />
                                    )}
                                  </span>
                                )}
                              {checkingAllModeration &&
                                !postsModerationResults[post.id] && (
                                  <Loader2
                                    size={14}
                                    className="animate-spin moderation-checking-icon"
                                  />
                                )}
                            </div>
                            <div className="desc">
                              {post.metaDescription || "Chưa có mô tả"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <b>{post.author?.fullName || "Chưa có tên"}</b>
                        <br />
                        <span className="small">
                          {post.author?.email || "Chưa có email"}
                        </span>
                      </td>

                      <td>{post.category?.name || "Chưa phân loại"}</td>
                      <td>{formatDateTime(post.createdAt)}</td>
                      <td>
                        <span className={`status-badge badge-${post.status}`}>
                          {post.status === "pending" && "Chờ duyệt"}
                          {post.status === "approved" && "Đã duyệt"}
                          {post.status === "rejected" && "Từ chối"}
                        </span>
                      </td>

                      <td>
                        <div className="card-actions">
                          <button
                            className="btn-edit"
                            title="Xem chi tiết"
                            onClick={() => openDetail(post)}
                          >
                            <Eye size={16} />
                          </button>

                          {/* Hiển thị nút Duyệt/Từ chối chỉ khi là tab pending */}
                          {tab === "pending" && (
                            <>
                              <button
                                className="btn-confirm"
                                title="Duyệt bài"
                                onClick={() => handleApprove(post.id)}
                              >
                                <CheckCircle size={16} />
                              </button>
                              <button
                                className="btn-danger"
                                title="Từ chối"
                                onClick={() => openReject(post)}
                              >
                                <XCircle size={16} />
                              </button>
                            </>
                          )}

                          {/* Nút từ chối cho bài viết đã duyệt */}
                          {tab === "approved" && (
                            <button
                              className="btn-danger"
                              title="Từ chối bài viết"
                              onClick={() =>
                                handleRejectApproved(post.id, post.title)
                              }
                            >
                              <XCircle size={16} />
                            </button>
                          )}

                          {/* Nút duyệt cho bài viết bị từ chối */}
                          {tab === "rejected" && (
                            <button
                              className="btn-confirm"
                              title="Duyệt bài viết"
                              onClick={() =>
                                handleApproveRejected(post.id, post.title)
                              }
                            >
                              <CheckCircle size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Phân trang */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="pagination-btn"
                  >
                    Trước
                  </button>
                  <span className="pagination-info">
                    Trang {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="pagination-btn"
                  >
                    Sau
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <img src={emptyDataImg} alt="Danh sách trống" />
              <p>
                {selectedCategory || searchQuery
                  ? `Không tìm thấy bài viết nào phù hợp với bộ lọc`
                  : `Chưa có bài viết nào trong mục ${
                      tab === "pending"
                        ? "Chờ duyệt"
                        : tab === "approved"
                        ? "Đã duyệt"
                        : "Từ chối"
                    }`}
              </p>
              {(selectedCategory || searchQuery) && (
                <button
                  onClick={() => {
                    setSelectedCategory("");
                    setSearchQuery("");
                  }}
                  className="clear-filter-btn"
                >
                  Xóa bộ lọc
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal chi tiết */}
      {showDetail && selectedPost && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowDetail(false);
            setSelectedPost(null);
          }}
        >
          <div
            className="modal-content-cencor"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-cencor">
              <h3 className="modal-title-cencor">
                {selectedPost.title || "Tiêu đề chưa có"}
              </h3>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setSelectedPost(null);
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="meta-row">
              <div>
                <b>ID: PM{selectedPost.id.toString().padStart(3, "0")}</b> •{" "}
                <b>{selectedPost.author?.fullName || "Chưa có tên"}</b> •{" "}
                <span className="small">
                  {selectedPost.author?.email || "Chưa có email"}
                </span>
              </div>
              <div>
                {selectedPost.status === "pending" && (
                  <span className="status-badge badge-pending">Chờ duyệt</span>
                )}
                {selectedPost.status === "approved" && (
                  <span className="status-badge badge-approved">Đã duyệt</span>
                )}
                {selectedPost.status === "rejected" && (
                  <span className="status-badge badge-rejected">Từ chối</span>
                )}
              </div>
            </div>

            {/* Cảnh báo kiểm duyệt nội dung */}
            {checkingModeration && (
              <div className="moderation-checking">
                <Loader2 className="animate-spin" size={16} />
                <span>Đang kiểm tra nội dung nhạy cảm...</span>
              </div>
            )}

            {moderationResult && !moderationResult.isSafe && (
              <div
                className={`moderation-warning ${
                  moderationResult.severity === "block" ? "block" : "warn"
                }`}
              >
                <div className="moderation-header">
                  {moderationResult.severity === "block" ? (
                    <AlertCircle className="moderation-icon" />
                  ) : (
                    <AlertTriangle className="moderation-icon" />
                  )}
                  <span className="moderation-title">
                    {moderationResult.severity === "block"
                      ? "Nội dung bị cấm"
                      : "Nội dung nhạy cảm"}
                  </span>
                </div>
                <div className="moderation-message">
                  {moderationResult.message}
                </div>
                {moderationResult.flaggedTerms &&
                  moderationResult.flaggedTerms.length > 0 && (
                    <div className="moderation-terms">
                      <strong>Từ khóa/hình ảnh vi phạm:</strong>
                      <div className="terms-list">
                        {moderationResult.flaggedTerms
                          .sort((a: string, b: string) => {
                            // Ưu tiên hiển thị từ ngữ "warn" trước "block"
                            const aIsWarn =
                              !a.startsWith("image:") &&
                              moderationResult.severity === "warn";
                            const bIsWarn =
                              !b.startsWith("image:") &&
                              moderationResult.severity === "warn";
                            if (aIsWarn && !bIsWarn) return -1;
                            if (!aIsWarn && bIsWarn) return 1;
                            return 0;
                          })
                          .map((term: string, index: number) => (
                            <span
                              key={index}
                              className={`term-badge ${
                                !term.startsWith("image:") &&
                                moderationResult.severity === "warn"
                                  ? "term-badge--warn"
                                  : "term-badge--block"
                              }`}
                            >
                              {term}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {moderationResult && moderationResult.isSafe && (
              <div className="moderation-safe">
                <Shield className="moderation-icon" />
                <span>Nội dung an toàn - Không phát hiện từ khóa nhạy cảm</span>
              </div>
            )}

            <div className="modal-field-row">
              <div className="modal-field">
                <div className="modal-title">
                  <Tag size={14} />
                  <b>Danh mục</b>
                </div>
                {selectedPost.category?.name || "Chưa phân loại"}
              </div>
              <div className="modal-field">
                <div className="modal-title">
                  <Calendar size={14} />
                  <b>Ngày gửi</b>
                </div>
                {formatDateTime(selectedPost.createdAt)}
              </div>
            </div>
            <div className="modal-field-row">
              <div className="modal-field">
                <div className="modal-title">
                  <Link size={14} />
                  <b>Đường dẫn:</b>
                </div>
                <span className="slug-text">{selectedPost.slug}</span>
              </div>
              <div className="modal-field">
                <div className="modal-title">
                  <View size={14} />
                  <b>Lượt xem:</b>
                </div>
                {selectedPost.viewCount}
              </div>
            </div>

            {selectedPost.featuredImage && (
              <div className="modal-field">
                <div className="modal-box">
                  <img
                    src={selectedPost.featuredImage}
                    alt={selectedPost.title}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              </div>
            )}

            <div className="modal-field">
              <div className="modal-title">
                <FileText size={14} />
                <b>Mô tả ngắn</b>
              </div>
              <div className="modal-box">
                {selectedPost.metaDescription || "Chưa có mô tả"}
              </div>
            </div>

            <div className="modal-field">
              <div className="modal-title">
                <b>Nội dung bài viết</b>
              </div>
              <div className="modal-box">
                {selectedPost.content || "Chưa có nội dung"}
              </div>
            </div>

            {selectedPost.primaryKeyword && (
              <div className="modal-field">
                <b>Từ khóa chính</b>
                <div className="modal-box">{selectedPost.primaryKeyword}</div>
              </div>
            )}

            {selectedPost.secondaryKeywords &&
              selectedPost.secondaryKeywords.length > 0 && (
                <div className="modal-field">
                  <b>Từ khóa phụ</b>
                  <div className="modal-box">
                    {selectedPost.secondaryKeywords.join(", ")}
                  </div>
                </div>
              )}

            {selectedPost.tags && selectedPost.tags.length > 0 && (
              <div className="modal-field">
                <b>Thẻ gắn</b>
                <div className="modal-box">{selectedPost.tags.join(", ")}</div>
              </div>
            )}

            {selectedPost.status === "pending" && (
              <div className="modal-actions">
                <button
                  className="btn-danger-cencor"
                  onClick={() => {
                    setShowRejectForm(true);
                    setShowDetail(false);
                  }}
                >
                  <XCircle size={16} /> Từ chối
                </button>
                <button
                  className="btn-primary-cencor"
                  onClick={() => {
                    handleApprove(selectedPost.id);
                  }}
                >
                  <CheckCircle size={16} /> Duyệt bài
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal xác nhận từ chối */}
      {showRejectForm && selectedPost && (
        <div className="modal-overlay" onClick={() => setShowRejectForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Từ chối bài viết</h3>
            <p>
              <b>{selectedPost.title}</b>
            </p>

            <textarea
              placeholder="Nhập lý do từ chối (bắt buộc)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="textarea-common"
            />

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowRejectForm(false)}
              >
                Hủy bỏ
              </button>
              <button className="btn-danger" onClick={handleConfirmReject}>
                <XCircle size={24} /> Xác nhận từ chối
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal xác nhận thay đổi trạng thái */}
      {showRevertModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowRevertModal(false);
            setRevertReason("");
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Xác nhận thay đổi trạng thái</h3>

            {revertAction === "approve" && (
              <>
                <p>
                  Bạn có chắc chắn muốn <b>duyệt</b> bài viết{" "}
                  <b>"{revertPostTitle}"</b>?
                </p>
                <div className="warning-text">
                  <div className="warning-icon-text">
                    <Check size={16} />
                    <span>
                      Bài viết sẽ được chuyển sang trạng thái "Đã duyệt" và có
                      thể được xuất bản.
                    </span>
                  </div>
                </div>
              </>
            )}

            {revertAction === "reject" && (
              <>
                <p>
                  Bạn có chắc chắn muốn <b>từ chối</b> bài viết{" "}
                  <b>"{revertPostTitle}"</b>?
                </p>
                <div className="warning-text">
                  <div className="warning-icon-text">
                    <XIcon className="warning-icon danger" size={16} />
                    <span>
                      Bài viết sẽ được chuyển sang trạng thái "Từ chối" và không
                      được xuất bản.
                    </span>
                  </div>
                </div>
              </>
            )}

            <div className="modal-field">
              <label>
                <b>Lý do {revertAction === "approve" ? "duyệt" : "từ chối"}:</b>
              </label>
              <textarea
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                placeholder={`Nhập lý do ${
                  revertAction === "approve" ? "duyệt" : "từ chối"
                } bài viết...`}
                rows={3}
                className="textarea-common"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowRevertModal(false);
                  setRevertReason("");
                }}
                disabled={loading}
              >
                Hủy
              </button>
              <button
                className={
                  revertAction === "approve" ? "btn-success" : "btn-danger"
                }
                onClick={confirmRevertAction}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Đang xử lý...
                  </>
                ) : (
                  <>
                    {revertAction === "approve" ? (
                      <>
                        <CheckCircle size={16} />
                        Duyệt bài viết
                      </>
                    ) : (
                      <>
                        <XCircle size={16} />
                        Từ chối bài viết
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal xác nhận duyệt hàng loạt */}
      {showBulkApprove && (
        <div
          className="modal-overlay"
          onClick={() => setShowBulkApprove(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Xác nhận duyệt hàng loạt</h3>
            <p>
              Bạn có chắc chắn muốn duyệt <b>{selectedIds.length}</b> bài viết
              đã chọn? Hành động này sẽ chuyển tất cả bài viết sang trạng thái
              "Đã duyệt".
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowBulkApprove(false)}
                disabled={loading}
              >
                Hủy
              </button>
              <button
                className="btn-success"
                onClick={onConfirmBulkApprove}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Đang duyệt...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Duyệt tất cả
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal xác nhận từ chối hàng loạt */}
      {showBulkReject && (
        <div className="modal-overlay" onClick={() => setShowBulkReject(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Xác nhận từ chối hàng loạt</h3>
            <p>
              Bạn có chắc chắn muốn từ chối <b>{selectedIds.length}</b> bài viết
              đã chọn? Hành động này sẽ chuyển tất cả bài viết sang trạng thái
              "Từ chối".
            </p>

            <div className="modal-field">
              <label>
                <b>Lý do từ chối (bắt buộc):</b>
              </label>
              <textarea
                value={bulkRejectReason}
                onChange={(e) => setBulkRejectReason(e.target.value)}
                placeholder="Nhập lý do từ chối cho tất cả bài viết..."
                rows={3}
                className="textarea-common"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowBulkReject(false)}
                disabled={loading}
              >
                Hủy
              </button>
              <button
                className="btn-danger"
                onClick={onConfirmBulkReject}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Đang từ chối...
                  </>
                ) : (
                  <>
                    <XCircle size={16} />
                    Từ chối tất cả
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Cencor;
